import { Autocomplete, Checkbox, Chip, FormHelperText, Grid, TextField, Tooltip, Typography, createFilterOptions } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import ButtonComponent from "../shared/button/button.component";
import CustomSwitch from "../shared/switch.component";
import * as selector from '../../selectors/selectors';
import { PhoneNumberModel, PhoneNumberStatus } from "../../models/phone-number.model";
import { UtilityService } from "../../services/UtilityService";
import { appNote, buttonLabels, ToasterTexts, validationsMessages } from "../shared/constants";
import { PhoneNumberService } from "../../services/PhonenumberService";
import { AutomateTransferCreditType } from "../../models/automate-transfer.model";
import { ToasterType } from "../shared/toaster/toaster.model";
import setToast from "../../helpers/toaster.helper";
import { NewInfoCircleIcon } from "../../icon";
import { useSelector } from 'react-redux';
import { PlanType } from "../../models/plans.model";

const creditOptions = [
    { Key: AutomateTransferCreditType.BOTH, Type: "Both" },
    { Key: AutomateTransferCreditType.ADDON_CREDITS_NORMAL, Type: "Add-on credits: 1-1" },
    { Key: AutomateTransferCreditType.ADDON_CREDITS_GROUP, Type: "Add-on credits: Group" }
];

const AutomateTransferComponent = () => {
    const Autofilter = createFilterOptions();

    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const IsDarkMode: boolean = useSelector(selector.isDarkMode)
    const PhoneNumbers: PhoneNumberModel[] = useSelector(selector.phoneNumbers)
    const [fromPhone, setFromPhone] = useState<string>("")
    const [toPhone, setToPhone] = useState<string[]>([])
    const [thresholdValue, setThresholdValue] = useState<any>()
    const [creditType, setCreditType] = useState<AutomateTransferCreditType | null>(null)
    const [creditTransferValue, setCreditTransferValue] = useState<any>()
    const [id, setID] = useState<string>()
    const [isSwitchEnabled, setSwitchEnabled] = useState(false)
    const [IsLoading, setIsLoading] = useState<boolean>(false)
    const [openDropdown, setOpenDropdown] = useState(false)
    const [isTouched, setIsTouched] = useState<boolean>(false)
    const [errors, setErrors] = useState<{
        SourcePhoneNumber?: string;
        TargetPhoneNumber?: string;
        CreditType?: string;
        TransferThreshold?: string;
        CreditAmount?: string;
    }>({
        SourcePhoneNumber: "",
        TargetPhoneNumber: "",
        CreditType: "",
        TransferThreshold: "",
        CreditAmount: "",
    })

    const recipientOptions = useMemo(() => {
        return PhoneNumbers.filter((item) => item.Status === PhoneNumberStatus.ACTIVE && item.Plans.some((plan) => plan.Plan.Type === PlanType.TIER && plan.Plan.Price >= 25)).filter(x => x.PhoneNumber !== fromPhone)
    }, [PhoneNumbers, fromPhone]);

    const handleSwitchChange = (checked: boolean) => {
        setSwitchEnabled(checked);
        if (checked === false) {
            setErrors({ SourcePhoneNumber: "", TargetPhoneNumber: "", CreditType: "", TransferThreshold: "", CreditAmount: "", })
        }
        setIsTouched(true)
    }

    const handlefromPhone = (event: any, value: PhoneNumberModel | null) => {
        setErrors({ ...errors, SourcePhoneNumber: "" })
        setFromPhone(value?.PhoneNumber || "");
        setIsTouched(true)
    }

    const handleChange = (event, value) => {
        if (value.some(x => x.value === -1)) {
            if (recipientOptions.length === toPhone.length) {
                setToPhone([])
            }
            else {
                setToPhone(recipientOptions.map(x => x.PhoneNumber))
                setErrors({ ...errors, TargetPhoneNumber: "" })
            }
        }
        else {
            setToPhone(value.map(x => x.PhoneNumber))
            setErrors({ ...errors, TargetPhoneNumber: "" })
        }
        setIsTouched(true)
    }

    const handleCreditType = (event: any, value: any) => {
        setErrors({ ...errors, CreditType: "" })
        console.log(value?.Key);
        
        setCreditType(value?.Key)
        setIsTouched(true)
    }

    const fetchCreditTransferRule = () => {
        PhoneNumberService.getCreditTransferRule()
            .then((res) => {
                const { fromPhoneNumber, toPhoneNumbers, threshold, type, transfer, id } = res.data[0];
                setFromPhone(fromPhoneNumber);
                setToPhone(toPhoneNumbers);
                setThresholdValue(threshold);
                setCreditType(type);
                setCreditTransferValue(transfer);
                setID(id);
                setSwitchEnabled(!!id);
            })
            .catch(() => {
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchCreditTransferRule();
    }, []);

    const handleTransfer = () => {
        setIsLoading(true)
        const obj: any = {
            "fromPhoneNumber": fromPhone,
            "toPhoneNumbers": toPhone,
            "threshold": thresholdValue,
            "type": creditType,
            "transfer": creditTransferValue,
        };
        if (id) {
            obj.id = id;
            obj.isActive = true;
            PhoneNumberService.updateCreditTransferRule(obj)
                .then(res => {
                    setToast(ToasterTexts.UPDATE_RULE, ToasterType.SUCCESS)
                    setIsLoading(false)
                })
                .catch(err => {
                    setIsLoading(false)
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
                })
        } else {
            PhoneNumberService.postCreditTransferRule(obj)
                .then(res => {
                    setToast(ToasterTexts.CREATE_RULE, ToasterType.SUCCESS)
                    fetchCreditTransferRule()
                    setIsLoading(false)
                })
                .catch(err => {
                    setIsLoading(false)
                    setToast(ToasterTexts.FILL_REQUIRED_FIELDS_IN_AUTO_CREDIT_RULE, ToasterType.WARNING)
                });
        }
        setIsTouched(false)
    }

    const deleteTransfer = () => {
        setIsLoading(true);
        PhoneNumberService.deleteCreditTransferRule(id)
            .then(() => {
                setFromPhone("");
                setToPhone([]);
                setThresholdValue("");
                setCreditType(null);
                setCreditTransferValue("");
                setID("");
                setSwitchEnabled(false);
                setToast(ToasterTexts.DELETE_RULE, ToasterType.SUCCESS);
            })
            .catch(() => {
                setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const validateForm = () => {
        let formValid = true;
        const newErrors = { ...errors };        
        if (fromPhone === "" || !fromPhone) {
            newErrors.SourcePhoneNumber = validationsMessages.TRANSFER_CREDITS_FROM_REQUIRED;
            formValid = false;
        }
        if (toPhone.length === 0) {
            newErrors.TargetPhoneNumber = validationsMessages.TRANSFER_CREDITS_TO_REQUIRED;
            formValid = false;
        }
        if (creditType === null) {
            newErrors.CreditType = validationsMessages.TRANSFER_CREDITS_TYPE_REQUIRED;
            formValid = false;
        }
        if (!thresholdValue) {
            newErrors.TransferThreshold = validationsMessages.TRANSFER_THRESHOLD_REQUIRED;
            formValid = false;
        }
        if (creditTransferValue !== 0 && !creditTransferValue) {
            newErrors.CreditAmount = validationsMessages.TRANSFER_CREDITS_AMOUNT_REQUIRED;
            formValid = false;
        }
        setErrors(newErrors);
        return formValid;
    }

    const handleButtonClick = (e: any) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        !isSwitchEnabled && id ? deleteTransfer() : handleTransfer();
    }

    const getButtonLabel = () => {
        return isSwitchEnabled && id ? buttonLabels.UPDATE : buttonLabels.SAVE;
    }

    const handleClear = () => {
        setFromPhone("");
        setToPhone([]);
        setThresholdValue("");
        setCreditType(null);
        setCreditTransferValue("");
        setErrors({
            SourcePhoneNumber: "",
            TargetPhoneNumber: "",
            CreditType: "",
            TransferThreshold: "",
            CreditAmount: "",
        })
        setIsTouched(false)
    }

    return (
        <>
            {!IsMobileDevice && <Grid container className={`${IsDarkMode ? "bg-color-primary-background" : "bg-color-settings-background"} h-p-95 m-auto`}>
                <div className={`w-fill d-flex flex-direction`}>
                    <div className={`w-p-60 settings-paper-border br-2 m-auto ${IsDarkMode ? "bg-color-sidebar-bg" : "paper-background"} my-8`}>
                        <div className={`bg-color-groupchat-avator-icon-bg`}>
                            <div className='d-flex align-center pt-3' >
                                <div className={`d-flex ml-4 `} style={{ flex: 1 }}>
                                    <Typography className="h3-semibold color-settings-heading">
                                        {appNote.AUTOMATE_CREDIT_TRANSFER}
                                    </Typography>
                                </div>
                                <div className={`pl-5 b-none d-flex `}>
                                    <span className={`d-flex `}>
                                        <div className={`d-flex mr-4`} style={{ flex: 1 }}>
                                            <CustomSwitch
                                                checked={isSwitchEnabled}
                                                onChange={(checked: boolean) => handleSwitchChange(checked)}
                                            />
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div className="report-text color-conv-section-inactive pb-2 pt-1 px-4" >
                                <div>Automatically transfer credits between phone numbers when a recipient's balance falls below <br />
                                    a set threshold, ensuring continuous messaging without manual intervention.</div>
                            </div>
                        </div>
                        <div className="border-top"></div>
                        <div className='d-flex align-center' style={{ gap: '16px' }}>
                            <div style={{ flex: 1 }}>
                                <div className='d-flex'>
                                    <span className='h4-semibold color-settings-heading pr-1 pl-4 pt-2'>Source Phone Number</span>
                                    <div className='d-flex align-center c-pointer'>
                                        <Tooltip title={"Enter the phone number from which credits will be transferred."} >
                                            <span style={{ marginTop: "16px" }}>
                                                <NewInfoCircleIcon />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                <Autocomplete
                                    fullWidth
                                    className={"w-fill pt-1 pl-4"}
                                    options={PhoneNumbers.filter(x => x.ID !== fromPhone && x.Status === PhoneNumberStatus.ACTIVE && !toPhone.includes(x.PhoneNumber))}
                                    getOptionLabel={(option) => option.PhoneNumber ? `${UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)} (${option.Name})` : ""}
                                    onChange={handlefromPhone}
                                    value={PhoneNumbers.find((x) => x.PhoneNumber === fromPhone) || null}
                                    renderOption={(defaultprops, option) => (
                                        <li {...defaultprops} className={"c-pointer h4-regular color-primary-dark p-1 ml-1 d-flex justify-between w-p-90"}>
                                            <div className="color-text-field-font">{UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)} ({option.Name})</div>
                                        </li>
                                    )}

                                    disabled={!isSwitchEnabled}
                                    classes={{
                                        input: "color-text-field-font h4-regular",
                                        paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow'
                                    }}
                                    tabIndex={16}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            name="from"
                                            size="small"
                                            variant="outlined"
                                            placeholder="From *"
                                            style={{ width: '90%' }}
                                            disabled={!isSwitchEnabled}
                                        />
                                    )}
                                />
                                {errors?.SourcePhoneNumber && (
                                    <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                        {errors?.SourcePhoneNumber}
                                    </FormHelperText>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className='d-flex'>
                                    <span className='h4-semibold color-settings-heading pl-2 pr-1 pt-2'>Recipient Phone Numbers</span>
                                    <div className='d-flex align-center c-pointer'>
                                        <Tooltip title={"Enter one or more phone numbers to receive credits."} >
                                            <span style={{ marginTop: "16px" }}>
                                                <NewInfoCircleIcon />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    disableCloseOnSelect
                                    open={openDropdown}
                                    onOpen={() => setOpenDropdown(true)}
                                    onClose={() => setOpenDropdown(false)}
                                    className={"w-fill pt-1 px-2 pr-4"}
                                    options={recipientOptions}
                                    getOptionLabel={(option) => option.Name}
                                    onChange={handleChange}
                                    classes={{ paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                    value={recipientOptions?.filter((x) => toPhone.includes(x.PhoneNumber))}
                                    filterOptions={(options: any[], params: any) => {
                                        if (recipientOptions.length > 0) {
                                            const filtered = Autofilter(options, params);
                                            return [{ Name: "Select all", value: -1 }, ...filtered];
                                        } else if (recipientOptions.length === 0) {
                                            const filtered = Autofilter(options, params);
                                            return [...filtered];
                                        }
                                        else { return options; }
                                    }}
                                    disabled={!isSwitchEnabled}
                                    renderOption={(defaultprops, option: any, { selected }: any) => {
                                        const selectAllProps =
                                            recipientOptions.length > 0 && option.value === -1
                                                ? { checked: (recipientOptions.length === toPhone.length) }
                                                : {};
                                        return <li {...defaultprops} className="d-flex align-center c-pointer">
                                            <Checkbox
                                                name="checkbox"
                                                size='small'
                                                className="ml-1"
                                                classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                                checked={selected}
                                                {...selectAllProps}
                                            />
                                            <div className="color-text-field-font h4-regular">{`${UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)} (${option.Name})`}</div>
                                        </li>
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            name="to"
                                            size="small"
                                            variant="outlined"
                                            placeholder={toPhone.length === 0 ? "To *" : ""}
                                            InputProps={{
                                                ...params.InputProps,
                                                style: {
                                                    flexWrap: "nowrap",
                                                    paddingRight: 0,
                                                }
                                            }}
                                            disabled={!isSwitchEnabled}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) => (
                                        <div className="d-flex o-x-auto align-center" style={{ flexWrap: 'nowrap', maxWidth: '230px' }} onClick={() => setOpenDropdown(true)}>
                                            {value.map((option, index) => (
                                                <Chip
                                                    classes={{
                                                        colorPrimary: "custom-color-chip-2",
                                                        avatarColorPrimary: "custom-chip-avatar",
                                                    }}
                                                    color="primary"
                                                    label={UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)}
                                                    {...getTagProps({ index })}
                                                    style={{ marginRight: '0px', maxWidth: '100%' }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                />
                                {errors?.TargetPhoneNumber && (
                                    <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                        {errors?.TargetPhoneNumber}
                                    </FormHelperText>
                                )}
                            </div>
                        </div>
                        <div className='d-flex align-center' style={{ gap: '16px' }}>
                            <div style={{ flex: 1 }}>
                                <div className='d-flex'>
                                    <span className='h4-semibold color-settings-heading pr-1 pl-4 pt-2'>Transfer Threshold</span>
                                    <div className='d-flex align-center c-pointer'>
                                        <Tooltip title={"Set the minimum credit balance at which a transfer should trigger."} >
                                            <span style={{ marginTop: "16px" }}>
                                                <NewInfoCircleIcon />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                <TextField
                                    fullWidth
                                    placeholder="Transfer Threshold Value *"
                                    size="small"
                                    type="number"
                                    variant="outlined"
                                    value={thresholdValue}
                                    onChange={(e) => {
                                        setErrors({ ...errors, TransferThreshold: "" }); setThresholdValue(e.target.value);
                                        setIsTouched(true)
                                    }}
                                    className="mt-1 ml-4"
                                    InputProps={{ classes: { input: 'h4-regular color-text-field-font' }, inputProps: { min: 1 } }}
                                    autoComplete='no'
                                    style={{ width: '82%' }}
                                    disabled={!isSwitchEnabled}
                                />
                                {errors?.TransferThreshold && (
                                    <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                        {errors?.TransferThreshold}
                                    </FormHelperText>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className='d-flex'>
                                    <span className='h4-semibold color-settings-heading pl-2 pr-1 pt-2'>Credit Type</span>
                                    <div className='d-flex align-center c-pointer'>
                                        <Tooltip title={"Specify the type of credits to transfer."} >
                                            <span style={{ marginTop: "16px" }}>
                                                <NewInfoCircleIcon />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                <Autocomplete
                                    classes={{
                                        input: "color-text-field-font h4-regular",
                                        paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow'
                                    }}
                                    tabIndex={16}
                                    fullWidth
                                    disableClearable
                                    className={"w-fill pt-1 px-2 pr-4"}
                                    options={creditOptions}
                                    getOptionLabel={(option) => option.Type}
                                    onChange={handleCreditType}
                                    value={creditOptions.find((x) => x.Key === creditType) || null}
                                    renderOption={(defaultProps, option) => (
                                        <li {...defaultProps} className={"c-pointer h4-regular color-text-field-font p-1 ml-1 d-flex justify-between w-p-90"}>
                                            <div>{option.Type}</div>
                                        </li>
                                    )}
                                    disabled={!isSwitchEnabled}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                            name="type"
                                            size="small"
                                            variant="outlined"
                                            placeholder="Credit Type *"
                                            disabled={!isSwitchEnabled}
                                        />
                                    )}
                                />
                                {errors?.CreditType && (
                                    <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                        {errors?.CreditType}
                                    </FormHelperText>
                                )}
                            </div>
                        </div>
                        <div className='w-p-50'>
                            <div style={{ flex: 1 }}>
                                <div className='d-flex'>
                                    <span className='h4-semibold color-settings-heading pr-1 pl-4 pt-2'>Credit Amount</span>
                                    <div className='d-flex align-center c-pointer'>
                                        <Tooltip title={"Specify the number of credits to transfer."} >
                                            <span style={{ marginTop: "16px" }}>
                                                <NewInfoCircleIcon />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                <TextField
                                    fullWidth
                                    placeholder="Credit Amount *"
                                    size="small"
                                    type="number"
                                    variant="outlined"
                                    value={creditTransferValue}
                                    onChange={(e) => {
                                        setErrors({ ...errors, CreditAmount: "" }); setCreditTransferValue(e.target.value);
                                        setIsTouched(true)
                                    }}
                                    className="mt-1 ml-4 w-p-80"
                                    InputProps={{ classes: { input: 'h4-regular color-text-field-font' }, inputProps: { min: 1 } }}
                                    autoComplete='no'
                                    disabled={!isSwitchEnabled}
                                />
                                {errors?.CreditAmount && (
                                    <FormHelperText className="h5 color-primary-danger t-align-right pr-5">
                                        {errors?.CreditAmount}
                                    </FormHelperText>
                                )}
                            </div>
                        </div>
                        <div className="d-flex float-right mt-3 mb-3 mr-3">
                            <div className="ml-2"><ButtonComponent OnClick={() => handleClear()} ButtonLabel={buttonLabels.CANCEL} ClassName={"px-3"} Variant={((isSwitchEnabled && isTouched) || (!isSwitchEnabled && isTouched && !!id)) ? "secondary-btn-small" : "disabled-btn-small"} /></div>
                            <div className="ml-2"><ButtonComponent ButtonLabel={getButtonLabel()} Loading={IsLoading} ClassName={"mr-1"} Variant={((isSwitchEnabled && isTouched) || (!isSwitchEnabled && isTouched && !!id)) ? "primary-btn-small" : "disabled-btn-small"} OnClick={handleButtonClick} /></div>
                        </div>
                    </div>
                </div>
            </Grid>
            }
            {IsMobileDevice &&
                <Grid container className={`bg-color-settings-background w-fill h-v-100 pt-3 px-2 o-y-scroll`}>
                    <div style={{ marginBottom: "100px" }}>
                        <div className={`bg-color-groupchat-avator-icon-bg`}>
                            <div className='d-flex align-center pt-3' >
                                <div className={`d-flex ml-4 `} style={{ flex: 1 }}>
                                    <Typography className="h3-semibold color-settings-heading">
                                        {appNote.AUTOMATE_CREDIT_TRANSFER}
                                    </Typography>
                                </div>
                                <div className={`pl-5 b-none d-flex `}>
                                    <span className={`d-flex `}>
                                        <div className={`d-flex mr-4`} style={{ flex: 1 }}>
                                            <CustomSwitch
                                                checked={isSwitchEnabled}
                                                onChange={(checked: boolean) => handleSwitchChange(checked)}
                                            />
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div className="report-text color-conv-section-inactive pb-2 pt-1 px-4" >
                                <div>Automatically transfer credits between phone numbers when a recipient's balance falls below a set threshold, ensuring continuous messaging without manual intervention.</div>
                            </div>
                        </div>
                        <div className="border-top"></div>
                        <div>
                            <div className='d-flex'>
                                <span className='h4-semibold color-settings-heading pr-1 pl-4 pt-2'>Source Phone Number</span>
                                <div className='d-flex align-center c-pointer'>
                                    <Tooltip enterTouchDelay={0} title={"Enter the phone number from which credits will be transferred."} >
                                        <span style={{ marginTop: "16px" }}>
                                            <NewInfoCircleIcon />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <Autocomplete
                                fullWidth
                                className={"w-fill pt-1 px-4"}
                                options={PhoneNumbers.filter(x => x.ID !== fromPhone && x.Status === PhoneNumberStatus.ACTIVE && !toPhone.includes(x.PhoneNumber))}
                                getOptionLabel={(option) => option.PhoneNumber ? `${UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)} (${option.Name})` : ""}
                                onChange={handlefromPhone}
                                value={PhoneNumbers.find((x) => x.PhoneNumber === fromPhone) || null}
                                renderOption={(defaultprops, option) => (
                                    <li {...defaultprops} className={"c-pointer h4-regular color-primary-dark p-1 ml-1 d-flex justify-between w-p-90"}>
                                        <div>{UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)} ({option.Name})</div>
                                    </li>
                                )}
                                disabled={!isSwitchEnabled}
                                classes={{
                                    input: "color-text-field-font h4-regular",
                                    paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow'
                                }}
                                tabIndex={16}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="from"
                                        size="small"
                                        variant="outlined"
                                        placeholder="From *"
                                        disabled={!isSwitchEnabled}
                                    />
                                )}
                            />
                            {errors?.SourcePhoneNumber && (
                                <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                    {errors?.SourcePhoneNumber}
                                </FormHelperText>
                            )}
                        </div>
                        <div>
                            <div className='d-flex'>
                                <span className='h4-semibold color-settings-heading pl-4 pr-1 pt-2'>Recipient Phone Numbers</span>
                                <div className='d-flex align-center c-pointer'>
                                    <Tooltip enterTouchDelay={0} title={"Enter one or more phone numbers to receive credits."} >
                                        <span style={{ marginTop: "16px" }}>
                                            <NewInfoCircleIcon />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <Autocomplete
                                multiple
                                fullWidth
                                disableCloseOnSelect
                                open={openDropdown}
                                onOpen={() => setOpenDropdown(true)}
                                onClose={() => setOpenDropdown(false)}
                                className={"w-fill pt-1 px-2 pr-4"}
                                options={recipientOptions}
                                getOptionLabel={(option) => option.Name}
                                onChange={handleChange}
                                classes={{ paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                                value={recipientOptions?.filter((x) => toPhone.includes(x.PhoneNumber))}
                                filterOptions={(options: any[], params: any) => {
                                    if (recipientOptions.length > 0) {
                                        const filtered = Autofilter(options, params);
                                        return [{ Name: "Select all", value: -1 }, ...filtered];
                                    } else if (recipientOptions.length === 0) {
                                        const filtered = Autofilter(options, params);
                                        return [...filtered];
                                    }
                                    else { return options; }
                                }}
                                disabled={!isSwitchEnabled}
                                renderOption={(defaultprops, option: any, { selected }: any) => {
                                    const selectAllProps =
                                        recipientOptions.length > 0 && option.value === -1
                                            ? { checked: (recipientOptions.length === toPhone.length) }
                                            : {};
                                    return <li {...defaultprops} className="d-flex align-center c-pointer">
                                        <Checkbox
                                            name="checkbox"
                                            size='small'
                                            className="ml-1"
                                            classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                            checked={selected}
                                            {...selectAllProps}
                                        />
                                        <div className="color-text-field-font h4-regular">{`${UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)} (${option.Name})`}</div>
                                    </li>
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="to"
                                        size="small"
                                        variant="outlined"
                                        placeholder={toPhone.length === 0 ? "To *" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: {
                                                flexWrap: "nowrap",
                                                paddingRight: 0,
                                            }
                                        }}
                                        disabled={!isSwitchEnabled}
                                    />
                                )}
                                renderTags={(value, getTagProps) => (
                                    <div className="d-flex o-x-auto align-center" style={{ flexWrap: 'nowrap', maxWidth: '230px' }} onClick={() => setOpenDropdown(true)}>
                                        {value.map((option, index) => (
                                            <Chip
                                                classes={{
                                                    colorPrimary: "custom-color-chip-2",
                                                    avatarColorPrimary: "custom-chip-avatar",
                                                }}
                                                color="primary"
                                                label={UtilityService.maskPhoneNumberOnLength(option.PhoneNumber)}
                                                {...getTagProps({ index })}
                                                style={{ marginRight: '0px', maxWidth: '100%' }}
                                            />
                                        ))}
                                    </div>
                                )}
                            />
                            {errors?.TargetPhoneNumber && (
                                <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                    {errors?.TargetPhoneNumber}
                                </FormHelperText>
                            )}
                        </div>
                        <div>
                            <div className='d-flex'>
                                <span className='h4-semibold color-settings-heading pr-1 pl-4 pt-2'>Transfer Threshold</span>
                                <div className='d-flex align-center c-pointer'>
                                    <Tooltip enterTouchDelay={0} title={"Set the minimum credit balance at which a transfer should trigger."} >
                                        <span style={{ marginTop: "16px" }}>
                                            <NewInfoCircleIcon />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <TextField
                                fullWidth
                                placeholder="Transfer Threshold Value *"
                                size="small"
                                type="number"
                                variant="outlined"
                                value={thresholdValue}
                                onChange={(e) => {
                                    setErrors({ ...errors, TransferThreshold: "" }); setThresholdValue(e.target.value);
                                }}
                                className="w-fill pt-1 px-4"
                                InputProps={{ classes: { input: 'h4-regular color-text-field-font' }, inputProps: { min: 1 } }}
                                autoComplete='no'
                                style={{ width: '82%' }}
                                disabled={!isSwitchEnabled}
                            />
                            {errors?.TransferThreshold && (
                                <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                    {errors?.TransferThreshold}
                                </FormHelperText>
                            )}
                        </div>
                        <div>
                            <div className='d-flex'>
                                <span className='h4-semibold color-settings-heading pl-4 pr-1 pt-2'>Credit Type</span>
                                <div className='d-flex align-center c-pointer'>
                                    <Tooltip enterTouchDelay={0} title={"Specify the type of credits to transfer."} >
                                        <span style={{ marginTop: "16px" }}>
                                            <NewInfoCircleIcon />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <Autocomplete
                                classes={{
                                    input: "color-text-field-font h4-regular",
                                    paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow'
                                }}
                                tabIndex={16}
                                fullWidth
                                disableClearable
                                className={"w-fill pt-1 px-4"}
                                options={creditOptions}
                                getOptionLabel={(option) => option.Type}
                                onChange={handleCreditType}
                                value={creditOptions.find((x) => x.Key === creditType) || null}
                                renderOption={(defaultProps, option) => (
                                    <li {...defaultProps} className={"c-pointer h4-regular color-text-field-font p-1 ml-1 d-flex justify-between w-p-90"}>
                                        <div>{option.Type}</div>
                                    </li>
                                )}
                                disabled={!isSwitchEnabled}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        name="type"
                                        size="small"
                                        variant="outlined"
                                        placeholder="Credit Type *"
                                        disabled={!isSwitchEnabled}
                                    />
                                )}
                            />
                            {errors?.CreditType && (
                                <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                    {errors?.CreditType}
                                </FormHelperText>
                            )}
                        </div>
                        <div>
                            <div className='d-flex'>
                                <span className='h4-semibold color-settings-heading pr-1 pl-4 pt-2'>Credit Amount</span>
                                <div className='d-flex align-center c-pointer'>
                                    <Tooltip enterTouchDelay={0} title={"Specify the number of credits to transfer."} >
                                        <span style={{ marginTop: "16px" }}>
                                            <NewInfoCircleIcon />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <TextField
                                fullWidth
                                placeholder="Credit Amount *"
                                size="small"
                                type="number"
                                variant="outlined"
                                value={creditTransferValue}
                                onChange={(e) => {
                                    setErrors({ ...errors, CreditAmount: "" }); setCreditTransferValue(e.target.value);
                                }}
                                className="w-fill pt-1 px-4"
                                InputProps={{ classes: { input: 'h4-regular color-text-field-font' }, inputProps: { min: 1 } }}
                                autoComplete='no'
                                disabled={!isSwitchEnabled}
                            />
                            {errors?.CreditAmount && (
                                <FormHelperText className="h5 color-primary-danger t-align-right pr-4">
                                    {errors?.CreditAmount}
                                </FormHelperText>
                            )}
                        </div>
                        <div className={`d-flex justify-between px-4 pt-6`} style={{ gap: '20px' }}>
                            <ButtonComponent
                                buttonProps={{ tabIndex: 6 }}
                                Variant={"secondary-btn-small"}
                                OnClick={handleClear}
                                ClassName={`c-pointer w-space w-fill justify-center mb-2 h2 `}
                                ButtonLabel={buttonLabels.CANCEL}
                            />
                            <ButtonComponent
                                buttonProps={{ tabIndex: 6 }}
                                Variant={"primary-btn-small"}
                                Loading={IsLoading}
                                ButtonLabel={getButtonLabel()}
                                OnClick={handleButtonClick}
                                ClassName={`c-pointer w-space w-fill justify-center mb-2 h2`}
                            />
                        </div>
                    </div>
                </Grid>
            }
        </>
    )
}
export default AutomateTransferComponent;